import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { baseUrl } from '../../constant/config';

const EXPLORER = 'https://developers.facebook.com/tools/explorer/';

// Only used until the server answers - the real list comes from META_SCOPES so
// the permissions shown here are exactly the ones the server checks for.
const FALLBACK_SCOPES = [
  'pages_show_list',
  'pages_read_engagement',
  'pages_manage_posts',
  'instagram_basic',
  'instagram_content_publish',
];

/**
 * Connect a Facebook token once, instead of editing .env every few hours.
 *
 * Meta's Graph API Explorer only gives out tokens that live for an hour or two.
 * The server exchanges whatever is pasted here for the Page token, which has no
 * expiry, and stores that - so this should only ever need doing once.
 */
export default function ConnectTokenDialog({ open, onClose, onDone }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState('');
  const [pageId, setPageId] = useState('');
  const [result, setResult] = useState(null);
  // Set when the account administers more than one Page and one must be chosen.
  const [pageChoices, setPageChoices] = useState(null);

  // Shared cache with the page itself, so this costs no extra request.
  const { data: targets } = useQuery(
    ['shareTargets'],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/share-targets`);
      return data.data;
    },
    { enabled: open, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000 }
  );
  const scopes = targets?.scopes?.length ? targets.scopes : FALLBACK_SCOPES;

  useEffect(() => {
    if (open) {
      setToken('');
      setPageId('');
      setResult(null);
      setPageChoices(null);
    }
  }, [open]);

  const {
    mutate: connect,
    isLoading,
    isError,
    error,
    reset,
  } = useMutation(
    async (chosenPageId) => {
      const body = chosenPageId
        ? { pageId: chosenPageId }
        : { token: token.trim(), ...(pageId.trim() ? { pageId: pageId.trim() } : {}) };
      const { data } = await axios.post(`${baseUrl}/api/videos/platform-connect`, body);
      return data;
    },
    {
      onSuccess: (data) => {
        if (data.data.needsPageSelection) {
          setPageChoices(data.data.pages);
          return;
        }
        setResult(data.data);
        queryClient.invalidateQueries('platformToken');
        queryClient.invalidateQueries('shareTargets');
        queryClient.invalidateQueries('platformPosts');
        onDone?.(data.message);
      },
    }
  );

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const message = error?.response?.data?.message || error?.message;

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect a Facebook token</DialogTitle>

      <DialogContent>
        {!result && !pageChoices && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Paste a token from Meta and the server will exchange it for a Page token, which does
              not expire. You should only need to do this once.
            </Alert>

            <Stepper orientation="vertical" activeStep={-1} sx={{ mb: 1 }}>
              <Step expanded>
                <StepLabel>
                  Open the{' '}
                  <Link href={EXPLORER} target="_blank" rel="noreferrer">
                    Graph API Explorer
                  </Link>{' '}
                  and pick your app
                </StepLabel>
              </Step>
              <Step expanded>
                <StepLabel>
                  <Box>
                    Add these permissions, then click Generate Access Token
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {scopes.map((s) => (
                        <Chip key={s} size="small" label={s} variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                </StepLabel>
              </Step>
              <Step expanded>
                <StepLabel>Paste the token below</StepLabel>
              </Step>
            </Stepper>

            <TextField
              label="Access token"
              placeholder="EAAB..."
              multiline
              minRows={3}
              fullWidth
              value={token}
              disabled={isLoading}
              onChange={(e) => setToken(e.target.value)}
              sx={{ mt: 1 }}
            />

            <TextField
              label="Page ID (optional)"
              helperText="Leave blank - the server lists the Pages this account administers and picks or asks"
              fullWidth
              size="small"
              value={pageId}
              disabled={isLoading}
              onChange={(e) => setPageId(e.target.value)}
              sx={{ mt: 2 }}
            />

            {isLoading && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
                Exchanging the token with Meta.
              </Alert>
            )}

            {isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <AlertTitle>Could not connect</AlertTitle>
                {message}
              </Alert>
            )}
          </>
        )}

        {pageChoices && !result && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Which Page should be connected?</AlertTitle>
              This account administers more than one. The token has already been exchanged - just
              pick the Page to post to.
            </Alert>
            <Stack spacing={1}>
              {pageChoices.map((pg) => (
                <Button
                  key={pg.id}
                  variant="outlined"
                  disabled={isLoading}
                  onClick={() => connect(pg.id)}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2">{pg.name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {pg.id}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
            {isLoading && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
                Connecting that Page.
              </Alert>
            )}
            {isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {message}
              </Alert>
            )}
          </>
        )}

        {result && (
          <>
            <Alert severity={result.neverExpires ? 'success' : 'warning'} sx={{ mb: 2 }}>
              <AlertTitle>
                {result.neverExpires
                  ? 'Connected - this token does not expire'
                  : 'Connected, but this token still expires'}
              </AlertTitle>
              {result.neverExpires
                ? 'Nothing needs to be put in the .env file, and this should not need doing again.'
                : `Meta still reports an expiry${
                    result.expiresAt ? ` of ${new Date(result.expiresAt).toLocaleString()}` : ''
                  }. That usually means the Page token could not be derived - check the steps below.`}
            </Alert>

            <Typography variant="subtitle2" gutterBottom>
              What the server did
            </Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {result.steps.map((step) => (
                <Stack key={step} direction="row" spacing={1} alignItems="flex-start">
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, mt: 0.3, color: 'success.main' }} />
                  <Typography variant="body2">{step}</Typography>
                </Stack>
              ))}
            </Stack>

            {result.tokenType && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                <strong>Token type:</strong> {result.tokenType}
              </Typography>
            )}
            {result.instagramConnected && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                <strong>Instagram:</strong> connected as {result.instagramUserId}
              </Typography>
            )}
            {result.missingScopes?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Missing {result.missingScopes.join(', ')} - publishing or deleting will be refused
                by Meta. Generate the token again with those permissions ticked.
              </Alert>
            )}

            {result.scopesKnown && result.scopes?.length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {result.scopes.map((s) => (
                  <Chip key={s} size="small" label={s} variant="outlined" />
                ))}
              </Stack>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {!result && !pageChoices && (
          <Button
            variant="contained"
            onClick={() => connect()}
            disabled={isLoading || !token.trim()}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isLoading ? 'Connecting...' : 'Connect'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

ConnectTokenDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};
